import React, { useState, useRef, useEffect, useCallback } from 'react';

import '../../stylesheets/components/PostcodeSelect.scss';

const SWIFTCOMPLETE_JS_URL = 'https://assets.swiftcomplete.com/js/swiftlookup.js';

const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export type FormattedAddress = {
  line_1: string;
  line_2: string;
  line_3: string;
  city: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
};

type SwiftcompletePostcodeSelectProps = {
  onSelect?: (address: FormattedAddress) => void;
  swiftcompleteApiKey?: string;
};

const SwiftcompletePostcodeSelect: React.FC<SwiftcompletePostcodeSelectProps> = ({ onSelect, swiftcompleteApiKey }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const line1Ref = useRef<HTMLInputElement>(null);
  const line2Ref = useRef<HTMLInputElement>(null);
  const townRef = useRef<HTMLInputElement>(null);
  const postcodeRef = useRef<HTMLInputElement>(null);
  const swiftLookupRef = useRef<any>(null);
  const selectedHandlerRef = useRef<EventListener | null>(null);
  const isMountedRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const detachFromSwiftcomplete = useCallback(() => {
    if (searchInputRef.current) {
      if (selectedHandlerRef.current) {
        searchInputRef.current.removeEventListener('swiftcomplete:swiftlookup:selected', selectedHandlerRef.current);
      }

      searchInputRef.current.onfocus = null;
      searchInputRef.current.onblur = null;
      searchInputRef.current.onkeyup = null;
      searchInputRef.current.onkeydown = null;
    }

    const dropdownContainer =
      swiftLookupRef.current?.container instanceof HTMLElement
        ? swiftLookupRef.current.container
        : document.querySelector('.swiftcompleteContainer');

    if (dropdownContainer && dropdownContainer.parentNode === document.body) {
      document.body.removeChild(dropdownContainer);
    }

    selectedHandlerRef.current = null;
    swiftLookupRef.current = null;
  }, []);

  const attachToSwiftcomplete = useCallback(() => {
    if (!swiftcompleteApiKey) return;
    if (!searchInputRef.current) return;

    const swiftcomplete = window.swiftcomplete;
    if (!swiftcomplete?.SwiftLookup) {
      setError('Address lookup unavailable. Please enter address manually.');
      return;
    }

    swiftcomplete.runWhenReady(() => {
      const instance = new swiftcomplete.SwiftLookup({
        key: swiftcompleteApiKey,
        field: searchInputRef.current,
        searchFor: 'address',
        emptyQueryMode: 'prompt',
        promptText: 'Start typing an address or postcode',
        scrollToFieldOnFocus: false,
        populateLineFormat: [
          { field: line1Ref.current, format: 'AddressLine1' },
          { field: line2Ref.current, format: 'AddressLine2' },
          { field: townRef.current, format: 'PrimaryLocality' },
          { field: postcodeRef.current, format: 'POSTCODE' },
        ],
      });

      swiftLookupRef.current = instance;
      instance.setCountries('gb');
      instance.load();

      const handleSelected: EventListener = (_event) => {
        const centre = swiftLookupRef.current?.selectedResult?.geometry?.centre;

        const formattedAddress: FormattedAddress = {
          line_1: line1Ref.current?.value || '',
          line_2: line2Ref.current?.value || '',
          line_3: '',
          city: townRef.current?.value || '',
          postal_code: postcodeRef.current?.value || '',
          latitude: typeof centre?.lat === 'number' ? centre.lat : null,
          longitude: typeof centre?.lng === 'number' ? centre.lng : null,
        };

        onSelectRef.current?.(formattedAddress);
      };

      selectedHandlerRef.current = handleSelected;
      searchInputRef.current.addEventListener('swiftcomplete:swiftlookup:selected', handleSelected);
    });
  }, [swiftcompleteApiKey]);

  useEffect(() => {
    isMountedRef.current = true;

    const initSwiftcomplete = async () => {
      if (!swiftcompleteApiKey) {
        if (isMountedRef.current) {
          setIsLoading(false);
          setError('Address lookup is not configured. Please enter address manually.');
        }
        return;
      }

      try {
        await loadScript(SWIFTCOMPLETE_JS_URL);
        if (isMountedRef.current) {
          setIsLoading(false);
          setTimeout(() => attachToSwiftcomplete(), 0);
        }
      } catch (err) {
        console.error('Failed to load Swiftcomplete SDK:', err);
        if (isMountedRef.current) {
          setIsLoading(false);
          setError('Failed to load address lookup. Please enter address manually.');
        }
      }
    };

    initSwiftcomplete();

    return () => {
      isMountedRef.current = false;
      detachFromSwiftcomplete();
    };
  }, [swiftcompleteApiKey, attachToSwiftcomplete, detachFromSwiftcomplete]);

  let className = 'PostcodeSelect';
  if (error) {
    className += ' has-error';
  }

  return (
    <div className={className}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder="Start typing an address or postcode"
          autoComplete="off"
          id="postcode_input"
          ref={searchInputRef}
          disabled={isLoading}
        />
        {isLoading && (
          <span className="input-group-append">
            <button className="btn btn-primary" disabled>Loading...</button>
          </span>
        )}
      </div>

      {error && <p className="help-block">{error}</p>}

      <input type="hidden" ref={line1Ref} />
      <input type="hidden" ref={line2Ref} />
      <input type="hidden" ref={townRef} />
      <input type="hidden" ref={postcodeRef} />
    </div>
  );
};

export default SwiftcompletePostcodeSelect;
